import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

// Dashboard "view as branch": attached ONLY to GET requests made while the dashboard is open.
// The backend re-validates it against the signed-in user's branch access (403 otherwise).
let dashBranch = "";
export const setDashboardBranch = (b) => { dashBranch = b || ""; };
api.interceptors.request.use((cfg) => {
  if (dashBranch && (cfg.method || "get").toLowerCase() === "get" && cfg.headers?.["X-Dashboard-Branch"] !== "") cfg.headers = { ...cfg.headers, "X-Dashboard-Branch": dashBranch };
  return cfg;
});

// CA client mode: the firm member works inside an authorised client. The header is attached only to finance
// endpoints; the server re-checks the client authorisation on every request (revocation is immediate).
const CA_KEY = "pawkt_ca_client";
const CA_PATHS = /^\/(vendors|vendor-bills|vendor-payments|purchase-requests|vendor-statements|fin\/|branches|ca\/client\/)/;
export const caClient = () => sessionStorage.getItem(CA_KEY) || "";
export const setCaClient = (id) => { if (id) sessionStorage.setItem(CA_KEY, id); else sessionStorage.removeItem(CA_KEY); };
api.interceptors.request.use((cfg) => {
  const ca = caClient();
  if (ca && CA_PATHS.test((cfg.url || "").replace(/^https?:\/\/[^/]+\/api/, ""))) cfg.headers = { ...cfg.headers, "X-CA-Client": ca };
  return cfg;
});

// Phase 9A — surface the server-side invoice setup gate from EVERY entry point.
// When any invoice-generating call returns 422 INVOICE_SETUP_REQUIRED, broadcast it so a
// single globally-mounted modal can prompt the business to complete setup.
api.interceptors.response.use(
  (r) => r,
  (e) => {
    const d = e?.response?.data?.detail;
    const status = e?.response?.status;
    if (status === 403 && d && typeof d === "object" && d.code === "CA_ACCESS_DENIED" && caClient()) {
      setCaClient("");
      window.dispatchEvent(new CustomEvent("ca-access-denied", { detail: d }));
    }
    if (status === 403 && d === "Branch not authorized" && dashBranch) {
      dashBranch = "";
      window.dispatchEvent(new CustomEvent("dashboard-branch-denied"));
    }
    if (status === 422 && d && typeof d === "object" && d.code === "INVOICE_SETUP_REQUIRED") {
      window.dispatchEvent(new CustomEvent("invoice-setup-required", { detail: d }));
    }
    // Plan entitlement gates (quantity limit 402, feature gate 403). One globally-mounted
    // dialog listens and presents the Pawkt upgrade experience.
    if (d && typeof d === "object" && (d.error === "PLAN_LIMIT_REACHED" || d.error === "FEATURE_NOT_AVAILABLE")) {
      window.dispatchEvent(new CustomEvent("plan-limit-reached", { detail: d }));
    }
    // Free Plan trial expired (server-enforced 402 on every protected API) -> full-page upgrade screen.
    if (status === 402 && d && typeof d === "object" && d.code === "TRIAL_EXPIRED") {
      window.dispatchEvent(new CustomEvent("trial-expired", { detail: d }));
    }
    return Promise.reject(e);
  }
);

export function apiErr(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  if (typeof d === "object") return d.msg || d.message || d.detail || d.error || d.code || "Request failed";
  return String(d);
}

export default api;
